import type { AdminFoodDetail, FoodDietClassName } from '@steado/admin-contract/foods';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { BanIcon, CircleCheckIcon, PencilIcon, TriangleAlertIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { BadgeCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api/api-error';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { FieldRow, FieldRows, Panel, TabSkeleton } from '@/components/panel/panels';
import { RecordHeader } from '@/components/panel/RecordHeader';
import { TabError } from '@/screens/user-detail/TabError';
import { formatDate, humanise } from '@/lib/format';
import { HistoryTab } from '@/screens/catalog-history/HistoryTab';
import { Figures, StatusBanner } from '@/screens/catalog-history/history-parts';
import {
  invalidateCatalog,
  subjectEventsQueryOptions,
} from '@/screens/catalog-history/catalog-history-query';

import { DietAnswer } from '@/screens/foods/DietAnswer';
import { JainAnswer } from '@/screens/foods/JainAnswer';

import {
  AllergensPanel,
  CurationPanel,
  NamesPanel,
  NoteSourcesPanel,
  NutritionPanel,
  PortionsPanel,
  ReviewBar,
  WhatChanges,
} from './FoodEditPanels';
import { MergeDialog } from './MergeDialog';
import { RecipePanel } from './RecipePanel';
import { ServingPolicyPanel } from './ServingPolicyPanel';
import { diffOf, draftOf, editProblems, patchOf, type EditDraft } from './food-edit-model';
import type { FoodDetailTab } from './food-detail-search';
import {
  deactivateFood,
  foodQueryOptions,
  reactivateFood,
  updateFood,
  verifyFood,
} from '@/screens/foods/foods-query';

interface FoodDetailScreenProps {
  readonly foodId: string;
  readonly tab: FoodDetailTab;
  /** Re-points the page, which a merge does: the survivor is a different row. */
  readonly onOpenFood: (foodId: string) => void;
  readonly onTabChange: (tab: FoodDetailTab) => void;
  /** Opens Catalog changes on an event, from History's "Open batch". */
  readonly onOpenEvent: (key: string) => void;
  /** Opens a food request, from History's "Open request". */
  readonly onOpenRequest?: ((id: string) => void) | undefined;
}

type OpenDialog = 'none' | 'deactivate' | 'merge' | 'verify';

/** Where a food came from, as History's first event names it. */
const SOURCE_NAMES: Record<AdminFoodDetail['source'], string> = {
  indb: 'Added from the Indian food database (INDB)',
  ifct: 'Added from the Indian food composition tables (IFCT)',
  open_food_facts: 'Added from Open Food Facts',
  ai_estimated: 'Added from a photo scan',
  manual: 'Added in the console',
  gym: 'Added by a gym',
};

function clock(date: Date): string {
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * One food, on its own page, in two tabs.
 *
 * **Details** is the editor: every fact the catalog records, staged in one
 * draft and written in one save, which lands in History as one change with
 * the operator's note and sources. Nothing is written until "What changes"
 * is confirmed, and Save waits while a figure is out of range or energy and
 * macros disagree by more than 10%. The recipe and the serving policy keep
 * their own saves; they are each one decision with their own check.
 *
 * **History** is every change to the food, its portions, names and recipe,
 * with Revert.
 */
export function FoodDetailScreen({
  foodId,
  tab,
  onOpenFood,
  onTabChange,
  onOpenEvent,
  onOpenRequest,
}: FoodDetailScreenProps) {
  const { api, can, admin } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery(foodQueryOptions(api, foodId));

  const [dialog, setDialog] = useState<OpenDialog>('none');
  const [reason, setReason] = useState('');
  const [jainAnswer, setJainAnswer] = useState<boolean | null>(null);
  const [dietAnswer, setDietAnswer] = useState<FoodDietClassName | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  /**
   * Every mutation returns the food the operator should now be looking at, so
   * the page is written straight from the response rather than refetched. The
   * list is invalidated because a verify or a deactivate changes a row on it,
   * and so are the scope counts, because it changes the review queue's depth.
   *
   * A merge returns a *different* food — the survivor — so the page follows it.
   */
  const onMutated = useCallback(
    (food: AdminFoodDetail) => {
      queryClient.setQueryData(['admin', 'food', food.id], food);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'foods'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'scope-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'catalog'] });
      setDialog('none');
      setReason('');

      if (food.id !== foodId) {
        queryClient.removeQueries({ queryKey: ['admin', 'food', foodId] });
        onOpenFood(food.id);
      }
    },
    [queryClient, foodId, onOpenFood],
  );

  const verify = useMutation({
    mutationFn: ({ jainExcluded, dietClass }: { jainExcluded: boolean; dietClass?: FoodDietClassName }) =>
      verifyFood(api, foodId, jainExcluded, dietClass),
    onSuccess: onMutated,
    retry: false,
  });

  const deactivate = useMutation({
    mutationFn: () => deactivateFood(api, foodId, reason),
    onSuccess: onMutated,
    retry: false,
  });

  const reactivate = useMutation({
    mutationFn: () => reactivateFood(api, foodId),
    onSuccess: onMutated,
    retry: false,
  });

  const food = query.data;
  // How many changes History holds, for the tab; the same query History reads its titles from.
  const historyEvents = useQuery({
    ...subjectEventsQueryOptions(api, 'food', food?.slug ?? ''),
    enabled: food !== undefined,
  });
  const historyCount = historyEvents.data?.length;

  /**
   * The staged edit, and the server value it was staged against.
   *
   * Adjusted during render rather than in an effect — the pattern React
   * documents for "reset state when a prop changes". Keyed on `updatedAt` so
   * the draft resets after our own save *and* if another operator's write
   * lands underneath: staged edits to a row that has moved are no longer
   * edits to anything.
   */
  const [seenAt, setSeenAt] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  if (food !== undefined && food.updatedAt !== seenAt) {
    setSeenAt(food.updatedAt);
    setDraft(draftOf(food));
    setConfirming(false);
  }

  const save = useMutation({
    mutationFn: (patch: ReturnType<typeof patchOf>) => updateFood(api, foodId, patch),
    onSuccess: (updated) => {
      onMutated(updated);
      invalidateCatalog(queryClient);
      setSaved(
        `Saved by ${admin?.displayName ?? admin?.email ?? 'you'} at ${clock(new Date())}. It is in History, where it can be reverted.`,
      );
    },
    retry: false,
  });

  const lines = food === undefined || draft === null ? [] : diffOf(food, draft);
  const problems = food === undefined || draft === null ? [] : editProblems(food, draft);
  const dirty = lines.length > 0;
  const isBusy = verify.isPending || deactivate.isPending || reactivate.isPending || save.isPending;
  const serverErrors = save.error instanceof ApiError ? save.error.fieldErrors() : {};

  // Opening "What changes" moves focus to it, so a keyboard user lands on the question.
  const reviewRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!confirming) return;
    const heading = reviewRef.current?.querySelector<HTMLElement>('#what-changes-title');
    heading?.scrollIntoView({ block: 'center' });
    heading?.focus();
  }, [confirming]);

  /** Unsaved edits survive a stray click on the back link. */
  const blocker = useBlocker({
    shouldBlockFn: () => dirty && !save.isPending,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  const name = food === undefined ? '' : (food.displayName ?? food.name);

  return (
    <div className="flex max-w-[1300px] flex-col gap-3.5 pb-4">
      {food === undefined ? null : (
        <RecordHeader
          label="Food summary"
          title={food.name}
          badges={
            <>
              <BadgeCell
                label={food.isVerified ? 'verified' : 'unverified'}
                tone={food.isVerified ? 'ok' : 'warn'}
                dot
              />
              <BadgeCell label={humanise(food.kind)} tone="mute" />
              {food.isActive ? null : <BadgeCell label="deactivated" tone="err" />}
              {dirty && tab === 'details' ? <BadgeCell label="Not saved" tone="warn" /> : null}
            </>
          }
          meta={[food.slug, humanise(food.source), `Updated ${formatDate(food.updatedAt)}`]}
          actions={
            <>
              {tab === 'history' ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    onTabChange('details');
                  }}
                >
                  <PencilIcon aria-hidden="true" className="size-3.5" />
                  Edit {name}
                </Button>
              ) : dirty ? (
                <Button
                  variant="ghost"
                  className="text-muted-foreground"
                  disabled={save.isPending}
                  onClick={() => {
                    setDraft(draftOf(food));
                    setConfirming(false);
                    save.reset();
                  }}
                >
                  Discard changes
                </Button>
              ) : null}
              <FoodActions
                food={food}
                canMutate={can.mutate}
                isBusy={isBusy}
                onVerify={() => {
                  setJainAnswer(null);
                  setDietAnswer(null);
                  setDialog('verify');
                }}
                onReactivate={() => {
                  reactivate.mutate();
                }}
                onDeactivate={() => {
                  setDialog('deactivate');
                }}
                onMerge={() => {
                  setDialog('merge');
                }}
              />
            </>
          }
          note={<ServabilityNote food={food} />}
          tabs={
            <nav aria-label={food.name} className="flex gap-1">
              {(['details', 'history'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-current={tab === value ? 'page' : undefined}
                  onClick={() => {
                    onTabChange(value);
                  }}
                  className={cn(
                    '-mb-px inline-flex h-10 items-center gap-1.5 border-b-2 px-2 text-[13px] transition-colors',
                    tab === value
                      ? 'border-foreground font-semibold text-foreground'
                      : 'border-transparent font-medium text-muted-foreground hover:text-foreground',
                  )}
                >
                  {value === 'details' ? 'Details' : 'History'}
                  {value === 'history' && historyCount !== undefined ? (
                    <span className="font-mono text-[12px] font-medium text-muted-foreground tabular-nums">
                      {historyCount >= 100 ? '100+' : historyCount}
                    </span>
                  ) : null}
                </button>
              ))}
            </nav>
          }
        />
      )}

      {query.isPending ? <TabSkeleton rows={4} /> : null}

      {query.isError ? <TabError message={query.error.message} onRetry={() => void query.refetch()} /> : null}

      {saved === null || tab !== 'details' ? null : (
        <StatusBanner
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onTabChange('history');
              }}
            >
              Open History
            </Button>
          }
        >
          <Figures className="font-medium" text={saved} />
        </StatusBanner>
      )}

      {food === undefined || tab !== 'history' ? null : (
        <HistoryTab
          subject="food"
          slug={food.slug}
          name={name}
          currentNames={food.aliases.map((alias) => alias.alias)}
          addedTitle={SOURCE_NAMES[food.source]}
          onOpenEvent={onOpenEvent}
          onOpenRequest={onOpenRequest}
        />
      )}

      {food === undefined || draft === null || tab !== 'details' ? null : (
        <>
          <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[11fr_10fr]">
            <div className="flex min-w-0 flex-col gap-3.5">
              <NutritionPanel
                food={food}
                draft={draft}
                disabled={!can.mutate || save.isPending}
                serverErrors={serverErrors}
                onChange={(patch) => {
                  setDraft({ ...draft, ...patch });
                }}
              />
              <PortionsPanel
                food={food}
                draft={draft}
                disabled={!can.mutate || save.isPending}
                serverErrors={serverErrors}
                onChange={(patch) => {
                  setDraft({ ...draft, ...patch });
                }}
              />
              <CurationPanel
                food={food}
                draft={draft}
                disabled={!can.mutate || save.isPending}
                serverErrors={serverErrors}
                onChange={(patch) => {
                  setDraft({ ...draft, ...patch });
                }}
              />
              <ServingPolicyPanel food={food} canMutate={can.mutate} onMutated={onMutated} />
            </div>
            <div className="flex min-w-0 flex-col gap-3.5">
              <NamesPanel
                // Its typed-but-not-added name resets with a save, as the draft does.
                key={`names-${food.updatedAt}`}
                food={food}
                draft={draft}
                disabled={!can.mutate || save.isPending}
                serverErrors={serverErrors}
                onChange={(patch) => {
                  setDraft({ ...draft, ...patch });
                }}
              />
              <AllergensPanel
                food={food}
                draft={draft}
                disabled={!can.mutate || save.isPending}
                serverErrors={serverErrors}
                onChange={(patch) => {
                  setDraft({ ...draft, ...patch });
                }}
              />
              <NoteSourcesPanel
                key={`sources-${food.updatedAt}`}
                draft={draft}
                disabled={!can.mutate || save.isPending}
                onChange={(patch) => {
                  setDraft({ ...draft, ...patch });
                }}
              />
              <RecipePanel food={food} canMutate={can.mutate} onMutated={onMutated} />
              <Panel title="Provenance" bleed>
                <FieldRows>
                  <FieldRow label="Source id" value={food.sourceId} mono />
                  <FieldRow label="Category" value={food.category} />
                  <FieldRow label="Added" value={formatDate(food.createdAt)} />
                </FieldRows>
              </Panel>
            </div>
          </div>

          {confirming && dirty && problems.length === 0 ? (
            <div ref={reviewRef}>
              <WhatChanges
                name={name}
                lines={lines}
                note={draft.note}
                sources={draft.sources.length}
                error={save.isError ? save.error.message : null}
                isSaving={save.isPending}
                onKeepEditing={() => {
                  setConfirming(false);
                }}
                onSave={() => {
                  save.mutate(patchOf(food, draft));
                }}
              />
            </div>
          ) : dirty ? (
            <ReviewBar
              problems={problems.length}
              changes={lines.length}
              disabled={!can.mutate}
              onReview={() => {
                setSaved(null);
                setConfirming(true);
              }}
            />
          ) : null}
        </>
      )}

      {[verify, reactivate]
        .filter((mutation) => mutation.isError)
        .map((mutation, index) => (
          <p key={index} role="alert" className="text-[12.5px] text-err-fg">
            {mutation.error.message}
          </p>
        ))}

      {dialog === 'verify' && food !== undefined ? (
        <ConfirmDialog
          title={`Verify ${food.name}?`}
          body={
            <div className="flex flex-col gap-3">
              <p>
                Its numbers show plainly in the app rather than as an estimate, and an estimate becomes
                plannable.
              </p>
              <JainAnswer value={jainAnswer} onChange={setJainAnswer} disabled={verify.isPending} count={1} />
              {food.source === 'ai_estimated' ? (
                <DietAnswer
                  value={dietAnswer}
                  provisional={food.dietClass}
                  onChange={setDietAnswer}
                  disabled={verify.isPending}
                />
              ) : null}
            </div>
          }
          confirmLabel="Verify"
          confirmDisabled={jainAnswer === null || (food.source === 'ai_estimated' && dietAnswer === null)}
          isPending={verify.isPending}
          error={verify.isError ? verify.error.message : undefined}
          onConfirm={() => {
            if (jainAnswer === null) return;
            verify.mutate(
              dietAnswer === null
                ? { jainExcluded: jainAnswer }
                : { jainExcluded: jainAnswer, dietClass: dietAnswer },
            );
          }}
          onCancel={() => {
            setDialog('none');
          }}
        />
      ) : null}

      {dialog === 'deactivate' && food !== undefined ? (
        <ConfirmDialog
          title={`Deactivate ${food.name}?`}
          body={
            <div className="flex flex-col gap-2.5">
              <p>
                It stops appearing in search. It is not deleted:{' '}
                <span className="font-mono">{String(food.usage.mealLogItems)}</span> meal log
                {food.usage.mealLogItems === 1 ? '' : 's'} point at it and keep the macros they were logged
                with.
              </p>
              <label className="flex flex-col gap-1 text-[12px] text-muted-foreground">
                Reason (recorded in the audit log)
                <input
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                  className="h-8 rounded-[7px] border border-border px-2.5 text-[12.5px] text-foreground outline-none focus:border-primary"
                />
              </label>
            </div>
          }
          confirmLabel="Deactivate"
          destructive
          isPending={deactivate.isPending}
          {...(deactivate.isError ? { error: deactivate.error.message } : {})}
          onConfirm={() => {
            deactivate.mutate();
          }}
          onCancel={() => {
            setDialog('none');
            setReason('');
          }}
        />
      ) : null}

      {dialog === 'merge' && food !== undefined ? (
        <MergeDialog
          food={food}
          onMerged={onMutated}
          onCancel={() => {
            setDialog('none');
          }}
        />
      ) : null}

      {blocker.status === 'blocked' ? (
        <ConfirmDialog
          title="Leave without saving?"
          body={
            <p>
              This food has edits that have not been saved. Leaving now discards them. The catalog keeps what
              it had.
            </p>
          }
          confirmLabel="Discard and leave"
          destructive
          onConfirm={() => {
            blocker.proceed();
          }}
          onCancel={() => {
            blocker.reset();
          }}
        />
      ) : null}
    </div>
  );
}

interface FoodActionsProps {
  readonly food: AdminFoodDetail;
  readonly canMutate: boolean;
  readonly isBusy: boolean;
  readonly onVerify: () => void;
  readonly onDeactivate: () => void;
  readonly onReactivate: () => void;
  readonly onMerge: () => void;
}

/**
 * Actions are rendered disabled for `support` rather than hidden, with the
 * reason on the control. Hiding them would leave a read-only operator unable to
 * tell whether an action exists at all, and the server refuses either way.
 */
function FoodActions({
  food,
  canMutate,
  isBusy,
  onVerify,
  onDeactivate,
  onReactivate,
  onMerge,
}: FoodActionsProps) {
  const title = canMutate ? undefined : 'Changing the catalog requires the admin role.';
  const disabled = !canMutate || isBusy;

  return (
    <>
      {food.isVerified ? null : (
        <Button variant="outline" onClick={onVerify} disabled={disabled} title={title}>
          Verify
        </Button>
      )}
      <Button variant="outline" onClick={onMerge} disabled={disabled} title={title}>
        Merge…
      </Button>
      {food.isActive ? (
        <Button variant="destructive" onClick={onDeactivate} disabled={disabled} title={title}>
          Deactivate…
        </Button>
      ) : (
        <Button variant="outline" onClick={onReactivate} disabled={disabled} title={title}>
          Reactivate
        </Button>
      )}
    </>
  );
}

/**
 * Whether a plan can serve this, and what already points at it.
 *
 * The rule lives in the plan shortlist query: a dish is servable as it is, a
 * raw ingredient only once somebody has given it a household measure —
 * without one it would reach a plan as "2.25 x 100 g" of itself. Every input
 * to that answer is somewhere on this page; stating it here saves the reader
 * assembling it from three cards.
 *
 * The usage counts ride along because they are the reason nothing here is ever
 * deleted, and an operator about to press Deactivate should see them first.
 */
function ServabilityNote({ food }: { readonly food: AdminFoodDetail }) {
  const hasHouseholdPortion = food.portions.some((portion) => portion.unit !== 'grams');
  const servable = food.kind === 'dish' || hasHouseholdPortion;

  const Icon = !food.isActive ? BanIcon : servable ? CircleCheckIcon : TriangleAlertIcon;
  const tone = !food.isActive ? 'text-err-fg' : servable ? 'text-muted-foreground' : 'text-warn-fg';

  return (
    <>
      <Icon aria-hidden="true" className={cn('size-4 shrink-0', tone)} />
      <span className="text-[13px] text-pretty text-muted-foreground">
        <span className={cn('font-medium', tone)}>
          {!food.isActive
            ? 'Deactivated — plans and search both skip it.'
            : servable
              ? 'Plans can serve this.'
              : 'Plans will not serve this yet: a raw ingredient needs a household measure.'}
        </span>{' '}
        <span className="font-mono">{String(food.usage.mealLogItems)}</span> meal log
        {food.usage.mealLogItems === 1 ? '' : 's'},{' '}
        <span className="font-mono">{String(food.usage.scans)}</span> scan
        {food.usage.scans === 1 ? '' : 's'} and{' '}
        <span className="font-mono">{String(food.usage.usedInDishes)}</span> dish
        {food.usage.usedInDishes === 1 ? '' : 'es'} already point at it, which is why nothing here is ever
        deleted.
      </span>
    </>
  );
}
